import { Calendar } from "@components/ui/calendar";
import Popover from "@components/ui/popover";
import { ScrollArea } from "@components/ui/scroll-area";
import { cn } from "@lib/cn";
import { useAnalyticsStoreShallow } from "@pages/analytics/store/analytics.store";
import { IconCalendar, IconCheck, IconSelector } from "@tabler/icons-react";
import moment from "moment";
import { memo, useCallback, useMemo, useState } from "react";
import type { DateRange } from "react-day-picker";

const OPTIONS = [
  {
    from: moment().format("YYYY-MM-DD"),
    to: moment().add(1, "days").format("YYYY-MM-DD"),
    label: "Today",
    index: 0,
  },
  {
    from: moment().subtract(1, "day").format("YYYY-MM-DD"),
    to: moment().format("YYYY-MM-DD"),
    label: "Yesterday",
    index: 1,
  },
  {
    from: moment().subtract(6, "days").format("YYYY-MM-DD"),
    to: moment().add(1, "days").format("YYYY-MM-DD"),
    label: "Last 7 days",
    index: 2,
  },
  {
    from: moment().subtract(29, "days").format("YYYY-MM-DD"),
    to: moment().add(1, "days").format("YYYY-MM-DD"),
    label: "Last 30 days",
    index: 3,
  },
  {
    from: moment().subtract(12, "month").format("YYYY-MM-DD"),
    to: moment().add(1, "days").format("YYYY-MM-DD"),
    label: "Last 12 months",
    index: 4,
  },
];

export const DateFilter = memo(
  ({
    overrideDate,
    setOverrideDate,
    view,
  }: {
    overrideDate?: {
      dateFrom: string;
      dateTo: string;
      index: number;
    };
    setOverrideDate?: (date: {
      dateFrom: string;
      dateTo: string;
      index: number;
    }) => void;
    view?: string;
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isCustomPickerOpen, setIsCustomPickerOpen] = useState(false);
    const { date, setDate } = useAnalyticsStoreShallow((s) => ({
      date: s.date,
      setDate: s.setDate,
    }));

    const currentDate = useMemo(() => {
      return overrideDate || date;
    }, [overrideDate, date]);

    const [dateRange, setDateRange] = useState<DateRange | undefined>({
      from: moment().subtract(29, "days").toDate(),
      to: moment().add(1, "days").toDate(),
    });

    const selectedOption = useMemo(() => {
      if (currentDate?.index === -1) {
        return "Custom";
      }
      return (
        OPTIONS.find((opt) => opt.index === currentDate.index)?.label || "Date"
      );
    }, [currentDate]);

    const handleOptionClick = useCallback(
      (index: number) => {
        if (index === -1) {
          // Custom option - close main popover and open custom picker
          setIsOpen(false);
          setIsCustomPickerOpen(true);
          return;
        }
        const option = OPTIONS[index];
        const payload = {
          dateFrom: option.from,
          dateTo: option.to,
          index: option.index,
        };
        if (setOverrideDate) setOverrideDate(payload);
        else setDate(payload);

        setIsOpen(false);
      },
      [setDate, setOverrideDate],
    );

    const handleCustomDateSelect = useCallback(
      (range: DateRange | undefined) => {
        if (range?.from && range?.to) {
          const payload = {
            dateFrom: moment(range.from).format("YYYY-MM-DD"),
            dateTo: moment(range.to).format("YYYY-MM-DD"),
            index: -1,
          };
          if (setOverrideDate) setOverrideDate(payload);
          else setDate(payload);
          setDateRange(range);
        }
      },
      [setDate, setOverrideDate],
    );

    return (
      <Popover open={isOpen} onOpenChange={setIsOpen} placement="bottom-start">
        <Popover.Trigger
          className={cn(
            "tw-flex tw-h-9 tw-cursor-pointer active:!tw-outline-offset-0 active:!tw-outline-1 active:!tw-outline-primary tw-items-center tw-shadow-2xs tw-gap-1 tw-rounded-lg tw-outline !tw-outline-1 !tw-outline-blue/5 tw-bg-white tw-px-2 tw-transition-colors !tw-outline-offset-0 tw-duration-100 hover:!tw-outline-primary -xl:tw-w-full -xl:tw-max-w-[700px]",
            {
              "!tw-outline-primary": isOpen,
              "-xl:tw-w-auto": view === "version-details",
            },
          )}
        >
          <span className="tw-flex tw-items-center tw-px-1 tw-font-sans tw-text-sm tw-font-medium tw-text-blue">
            <IconCalendar className="tw-mr-1.5 tw-text-blue" />
            {selectedOption}
          </span>
          <IconSelector className="tw-size-4 tw-text-blue-500 tw-ml-auto" />
        </Popover.Trigger>
        <Popover.Content className="tw-rounded-lg tw-z-[99999] tw-outline !tw-outline-1 !tw-outline-blue/5 tw-bg-white tw-shadow-md">
          <ScrollArea maxHeight={250}>
            <div className="tw-p-1 tw-space-y-0.5 tw-max-w-full tw-min-w-0">
              {OPTIONS.map((option) => (
                <button
                  type="button"
                  key={option.index}
                  onClick={() => handleOptionClick(option.index)}
                  className={cn(
                    "tw-flex tw-h-9 tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-rounded tw-px-2 tw-font-sans tw-text-sm tw-text-blue tw-outline-none tw-transition-colors hover:tw-bg-blue-50",
                    {
                      "tw-bg-blue-100 hover:tw-bg-blue-100":
                        date.index === option.index,
                    },
                  )}
                >
                  <span className="tw-flex-1 tw-min-w-0 tw-text-left tw-truncate tw-whitespace-nowrap">
                    {option.label}
                  </span>
                  {date.index === option.index && (
                    <IconCheck className="tw-size-4 tw-text-blue tw-ml-3 tw-flex-shrink-0" />
                  )}
                </button>
              ))}
              <Popover
                open={isCustomPickerOpen}
                onOpenChange={setIsCustomPickerOpen}
                placement="right"
              >
                <Popover.Trigger asChild>
                  <button
                    type="button"
                    className={cn(
                      "tw-flex tw-h-9 tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-rounded tw-px-2 tw-font-sans tw-text-sm tw-text-blue tw-outline-none tw-transition-colors hover:tw-bg-blue-50",
                      {
                        "tw-bg-blue-50 hover:tw-bg-blue-50":
                          date.index !== -1 && isCustomPickerOpen,
                        "tw-bg-blue-100 hover:tw-bg-blue-100":
                          date.index === -1,
                      },
                    )}
                  >
                    <span className="tw-flex-1 tw-min-w-0 tw-text-left tw-truncate tw-whitespace-nowrap">
                      Custom
                    </span>
                    {date.index === -1 && (
                      <IconCheck className="tw-size-4 tw-text-blue tw-ml-3 tw-flex-shrink-0" />
                    )}
                  </button>
                </Popover.Trigger>
                <Popover.Content className="tw-z-[99999] tw-pl-1">
                  <Calendar
                    mode="range"
                    defaultMonth={dateRange?.from || new Date()}
                    selected={dateRange}
                    numberOfMonths={1}
                    onSelect={handleCustomDateSelect}
                    disabled={{
                      before: moment().subtract(2, "years").toDate(),
                    }}
                  />
                </Popover.Content>
              </Popover>
            </div>
          </ScrollArea>
        </Popover.Content>
      </Popover>
    );
  },
);
